<template>
    <div ref="mapContainer" class="base-map" />
</template>

<script setup lang="ts">
import type { FillableOrder, MapPadding } from '@passengerkit/shared/types/types';
import polyline from "@mapbox/polyline"
// Type-only imports are erased at build time, so they're safe under SSR.
// maplibre-gl itself is browser-only and is dynamically imported in onMounted.
import type { Map as MlMap, Marker as MlMarker, GeoJSONSource } from 'maplibre-gl'

const config = useRuntimeConfig()

const mapCenter = ref<[number, number]>([0, 0])
const mapZoom = ref(15)

const { newOrder, padding } = defineProps<{
    newOrder?: FillableOrder,
    padding?: MapPadding
}>()

const mapContainer = ref<HTMLElement | null>(null)
const map = ref<MlMap | null>(null)
// Held so drawFeatures() can construct markers without re-importing maplibre-gl.
let MarkerCtor: typeof MlMarker | null = null

const paddingToWatch = computed(() => {
    return padding
})

watch(paddingToWatch, (val) => {
    if(val == undefined){
        return
    }
    map.value?.easeTo({
        padding: val,
        duration: 1000 // In ms. This matches the CSS transition duration property.
    });
})

onMounted(async () => {
    if (!mapContainer.value) {
        return
    }

    const maplibre = await import('maplibre-gl')
    MarkerCtor = maplibre.Marker

    map.value = new maplibre.Map({
        container: mapContainer.value,
        style: config.public.mapStyleUrl,
        center: mapCenter.value,
        zoom: mapZoom.value,
    })

    map.value.on('load', () => {
        if (padding != undefined) {
            map.value?.easeTo({
                padding: padding,
                duration: 0 // In ms. This matches the CSS transition duration property.
            });
        }
        drawFeatures()
    })
})

onBeforeUnmount(() => {
    map.value?.remove()
    map.value = null
})

const firstTimeDrawingFeatures = ref(true)


const newOrderPickupMarker = ref<MlMarker | null>(null)
const newOrderDestinationMarker = ref<MlMarker | null>(null)

watch(() => newOrder, () => {
    drawFeatures()
}, {deep: true})

function drawFeatures(){
    // Sources/layers require a loaded style; the map's 'load' handler re-runs
    // this once the style is ready, so early calls simply no-op.
    if(!map.value || !map.value.isStyleLoaded()){
        return
    }
    displayNewOrderOnMap()
    firstTimeDrawingFeatures.value = false
}

function displayNewOrderOnMap(){
    newOrderPickupMarker.value?.remove()
    newOrderDestinationMarker.value?.remove()

    if(!map.value || !MarkerCtor){
        return
    }

    if (
        typeof newOrder?.pickupLocation?.name == 'undefined'
        && typeof newOrder?.destinationLocation?.name == 'undefined'
    ) {
        return
    }

    var pickupMarkerPos: [number, number] | null = null


    if(newOrder?.pickupLocation?.latitude){
        pickupMarkerPos = [newOrder!.pickupLocation!.longitude, newOrder!.pickupLocation!.latitude]
        newOrderPickupMarker.value = new MarkerCtor()
            .setLngLat(pickupMarkerPos)
            .addTo(map.value);
    }

    var destinationMarkerPos: [number, number] | null = null

    if(newOrder?.destinationLocation?.latitude){
        destinationMarkerPos = [newOrder!.destinationLocation!.longitude, newOrder!.destinationLocation!.latitude]
        newOrderDestinationMarker.value = new MarkerCtor()
            .setLngLat(destinationMarkerPos)
            .addTo(map.value);
    }

    if(newOrder?.route){
        // Route geometry comes from the API's Valhalla proxy as a precision-6 polyline.
        const geojson = polyline.toGeoJSON(newOrder.route.geometry, 6);

        const existingSource = map.value.getSource('newOrderRoute') as GeoJSONSource | undefined
        if(existingSource){
            existingSource.setData(geojson as any)
        }
        else{
            map.value.addSource('newOrderRoute', {
                'type': 'geojson',
                'data': geojson as any
            })
        }

        if(!map.value.getLayer('newOrderRouteLayer')){
            map.value.addLayer({
                id: 'newOrderRouteLayer',
                type: 'line',
                source: 'newOrderRoute',
                paint: {
                    'line-color': '#0099ff',
                    'line-width': 8
                }
            });
        }
    }

    if(pickupMarkerPos != null && destinationMarkerPos != null){
        mapZoom.value = 6
        mapCenter.value = [
            (pickupMarkerPos[0] + destinationMarkerPos[0]) / 2,
            (pickupMarkerPos[1] + destinationMarkerPos[1]) / 2
        ]

        map.value?.fitBounds([
            pickupMarkerPos,
            destinationMarkerPos
        ], {
            padding: 100,
            duration: firstTimeDrawingFeatures.value? 0 : 2000
        })
    }
    else if(pickupMarkerPos != null){
        mapZoom.value = 15
        mapCenter.value = pickupMarkerPos
        map.value.flyTo({
            center: pickupMarkerPos,
            zoom: mapZoom.value,
        })

        if(firstTimeDrawingFeatures.value){
            map.value.setCenter(pickupMarkerPos)
            map.value.setZoom(mapZoom.value)
        }
    }
    else if(destinationMarkerPos != null){
        mapZoom.value = 15
        mapCenter.value = destinationMarkerPos
        map.value.flyTo({
            center: destinationMarkerPos,
            zoom: mapZoom.value,
        })

        if (firstTimeDrawingFeatures.value) {
            map.value.setCenter(destinationMarkerPos)
            map.value.setZoom(mapZoom.value)
        }
    }
}

</script>

<style lang="scss">
.base-map {
    height: 100%;
    width: 100%;
}
</style>
